import React, { useRef, useState } from 'react';
import UploadModal from '../modal/UploadModal';
import Button from '../ui/Button';

interface PhotoUploadProps {
  onPhotoSelect: (file: File) => void;
  /**
   * Called when the user picks an existing asset instead of uploading a file. Receives the
   * asset's URL. Only wired up when `artistId` is provided (i.e. editing an existing artist),
   * since a not-yet-created artist has no assets to choose from.
   */
  onAssetSelect?: (url: string) => void;
  preview?: string;
  /** Present only when editing an existing artist - enables the shared asset-management modal. */
  artistId?: string;
}

const VALID_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/webp',
  'image/svg+xml',
];

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  onPhotoSelect,
  onAssetSelect,
  preview,
  artistId,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // The shared Asset Management modal only makes sense for an existing artist that owns assets.
  // Creating a new artist keeps the plain "browse -> upload with the form" behaviour.
  const canUseAssetModal = Boolean(artistId && onAssetSelect);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so selecting the same file again still fires onChange.
    e.target.value = '';
    if (!file) return;

    if (!VALID_TYPES.includes(file.type)) {
      alert('Please select a valid image file (JPG, PNG, GIF, BMP, WEBP, SVG).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit.');
      return;
    }
    onPhotoSelect(file);
  };

  // With no assets modal (creating a new artist), keep the direct-to-file-browser behaviour.
  const handleButtonClick = () => {
    if (canUseAssetModal) {
      setIsModalOpen(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  // UploadModal reports the current selection as an array; for a profile picture we only take
  // the first (and, via maxTotalImages=1, only) chosen asset and hand its URL to the parent.
  const handleSelectionChange = (
    selected: { url: string; imageTitle: string }[]
  ) => {
    if (selected.length > 0) {
      onAssetSelect?.(selected[0].url);
      setIsModalOpen(false);
    }
  };

  return (
    <div className="flex items-center space-x-4">
      <div className="w-24 h-24 overflow-hidden border-2 border-dashed rounded-lg bg-primary-50 border-primary-200">
        {preview ? (
          <img src={preview} alt="Preview" className="object-cover w-full h-full" />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <svg
              className="w-8 h-8 text-primary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </div>
        )}
      </div>

      <div>
        <Button type="button" variant="secondary" onClick={handleButtonClick}>
          {preview ? 'Change Photo' : 'Upload Photo'}
        </Button>
        <p className="mt-1 text-xs text-neutral-500">
          JPG, PNG, GIF, BMP, WEBP, SVG - max 10MB
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/bmp,image/webp,image/svg+xml"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Reuse the shared Asset Management modal (upload + pick from assets) rather than a
          bespoke picker. maxTotalImages=1 keeps it a single profile-picture selection. */}
      {canUseAssetModal && (
        <UploadModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          artistId={artistId}
          onSelectionChange={handleSelectionChange}
          showCheckboxes
          maxTotalImages={1}
          initialSelectedImages={[]}
        />
      )}
    </div>
  );
};

export default PhotoUpload;
